import skjematekst_nb from "../nb/skjematekst";
import {
  DatakrimSvarType,
  DatakrimType,
  HarBankenStansetOverforingType,
  KjennemerkeKategori,
  KjennemerketypeType,
  TilegnetVerdiType,
} from "@politiet/anmeldelse-lib/types/anmeldelseUtkast.typer";
import {
  AnmeldelseType as DigaAnmeldelseType,
  BrukstyveriType,
  GjerningsstedType,
  SkadeverkType,
} from "@politiet/anmeldelse-lib/types/digaAnmeldelseBegrenset.typer";
import { SVVFargekode } from "../../skjema/anmeldelse/gjerning/Handlinger/gjenstander/components/felles/kjoretoy/SVVFargekoder.typer";

const skjematekst: typeof skjematekst_nb = {
  felles: {
    anmeldelseType: {
      TYVERI_SKADEVERK_INNBRUDD: "Theft, burglary and damaged property",
      BEDRAGERI: "Fraud",
    },
    stegvelger: {
      ferdig: {
        ariaLabel: "Step completed",
      },
      navbar: {
        ariaLabel: "Steps",
      },
    },
    virksomhet: {
      orgnummer: "Organization number",
      valgtVirksomhet: {
        slett: "Delete",
        beliggenhetsadresse: "Enterprise address",
        aktivitet: "Activity",
      },
      slettVirksomhet: "Delete enterprise",
    },
    knapper: {
      rediger: {
        label: "Edit",
        ariaLabel: "Edit",
        ariaLabelIndexed: "Edit number",
      },
      lukk: {
        ariaLabel: "Close",
      },
    },
    feilmelding: {
      ikon: {
        ariaLabel: "Error message",
      },
      sum: {
        kunTallITallfelt:
          "This field can only contain numbers. Other characters such as commas, periods and letters are not allowed. If you are trying to enter a decimal number, round it to the nearest whole number.",
      },
    },
    components: {
      pagination: {
        forrige: {
          ariaLabel: "Previous page",
        },
        numerertSide: {
          ariaLabel: "Go to page {{side}}",
        },
        neste: {
          ariaLabel: "Next page",
        },
      },
      sokefelt: {
        ariaLabel: "Search",
      },
      modal: {
        ariaLabel: "Close window",
      },
      erstatningskrav: {
        ariaLabel: "Compensation claim documentation",
      },
      handelsvarer: {
        slett: {
          ariaLabel: "Delete item",
        },
      },
      autocomplete: {
        ingenTreff: "No search results",
        laster: "Loading...",
      },
      inputDate: {
        toggleDatoVelgerKnapp: {
          closed: "Open date selector",
          open: "Close date selector",
        },
        placeholder: "dd.mm.yyyy",
        errorMessages: {
          parseError: "Could not parse the date, use the format dd.mm.yyyy",
        },
        lukkKnapp: "Done",
        labels: {
          labelDayButton_today: "Today, {{label}}",
          labelDayButton_selected: "{{label}} selected",
          labelWeekNumber: "Week {{weekNumber}}",
          labelNext: "Next month",
          labelPrevious: "Previous month",
          labelMonthDropdown: "Select month",
          labelYearDropdown: "Select year",
        },
      },
      inputDateTime: {
        timePicker: {
          timeLabel: "Hour",
          timeAriaLabel: "Select hour",
          minuttLabel: "Minute",
          minuttAriaLabel: "Select minute",
        },
        placeholder: "dd.mm.yyyy hh:mm",
        errorMessages: {
          parseError:
            "Could not parse the date, use the format dd.mm.yyyy hh:mm",
        },
      },
    },
  },
  feilside: {
    notFound: {
      tittel: "Sorry, we can't find this page.",
      info: "You may need to log in to view the page, or it may not exist. Have you checked that the address is written correctly?",
      infoInnlogget: "Have you checked that the address is written correctly?",
    },
    forbidden: {
      tittel: "You do not have access to view this page",
      info: "Have you checked that the address is written correctly?",
    },
    default: {
      tittel: "Something went wrong",
      info: "Please reload the page or try again later.",
    },
    knapper: {
      tjenester: "See police services",
      forsiden: "Go to the front page",
      logginn: "Log in",
      lastInn: "Reload the page",
    },
  },
  anmeldelse: {
    autoslettMelding_one: "The draft will be deleted in {{count}} day.",
    autoslettMelding_other: "The draft will be deleted in {{count}} days.",
    av_anmeldelse_nummer: "of report number",
    chips: {
      INNBRUDD: "Burglary",
      SKADEVERK: "Damaged property",
      TYVERI: "Theft",
      BEDRAGERI: "Fraud",
      DATAKRIM: "Cybercrime",
    },
    fornaermede_mangler: "The victim is missing",
    lovbrudd_mangler: "-- Offense missing --",
    fortsett_utfylling: "Continue filling in",
    fra: "from",
    gaa_til_anmeldelse: "Go to report",
    gjerningTyper: {
      INNBRUDD: "Burglary",
      SKADEVERK: "Damaged property",
      TYVERI: "Theft",
    },
    ingen_anmeldelser_innsendt: "-- No submitted reports --",
    ingen_anmeldelser_paabegynt: "-- No drafts --",
    kort: {
      diga: {
        chip: "Old report",
        type: {
          TYVERI_AV_SYKKEL: "Theft of bike",
          TYVERI_AV_MOBILTELEFON: "Theft of mobile phone",
          TYVERI_FLERE_GJENSTANDER: "Theft of other items",
          KJENNEMERKE: "Theft of licence plate",
          SKADEVERK: "Vandalism to private property, objects or vehicle",
          KRENKELSE: "Illegal use of real property",
          BRUKSTYVERI: "Theft or unauthorized use of vehicle or boat",
        } satisfies Record<DigaAnmeldelseType, string>,
      },
      skjema: {
        SKADEVERK: {
          gjenstand: {
            label_zero: "Damaged property",
            label_one:
              "Damaged $t(anmeldelse.kort.skjema.gjenstandTyper.{{gjenstandType}})",
            label_other: "Damage to multiple objects",
          },
        },
        TYVERI: {
          gjenstand: {
            label_zero: "Theft",
            label_one:
              "Theft of $t(anmeldelse.kort.skjema.gjenstandTyper.{{gjenstandType}})",
            label_other: "Theft of multiple items",
          },
        },
        gjenstandTyper: {
          ANNET: "Other",
          FARTOY: "Vessel",
          HANDELSVARER: "Item list",
          KJENNEMERKE: "License plate",
          KJOERETOEY: "Vehicle",
          MOBILTELEFON: "Phone",
          SYKKEL: "Bicycle",
          ELEKTRONIKK: "Electronics",
          BYGNING_EIENDOM: "Building or property",
          VERKTOY: "Tools",
        },
      },
      og: "and",
    },
    nummer: "number",
    saksnummer: "Case number",
    sendt_inn: "Submitted",
    sist_endret: "Last modified",
    statusTyper: {
      UTKAST: "Draft",
    },
  },
  auth: {
    min_profil: {
      ariaLabel: "Information about your user",
      kontaktInfo: "Contact information to the police",
      knapper: {
        logg_ut: "Log out",
      },
    },
    begrensetTilgang: {
      innlogget: {
        tittel: "You do not have access to digitally report a crime",
        isNotInnloggetMedGjeldendePid: {
          info: "You are logged in with an identifier that has expired. You must log in with the identifier that is valid in the National Population Register.",
        },
      },
    },
    erDuHerFortsatt: {
      forbliKnapp: "Stay logged in",
      info: "You have been quiet for a while. If you do not do anything active on this page, you will soon be logged out.",
      loggUtKnapp: "Log out now",
      tittel: "Are you still here?",
    },
    idPortenBar: {
      kontaktPolitiet: "Contact the police",
      loggetInnSom: "Logged in as",
      nodnummer: "Emergency number",
      ringPolitiet: "Call the police",
    },
    representasjon: {
      altinnFeil: "Unable to retrieve roles from Altinn",
      fnr: "id number: {{fnr, fnr}}",
      ingenAaRepresenetere:
        "Based on roles in Altinn you can not represent anyone other than yourself",
      megSelv: "(myself)",
      orgnr: "org. No.: {{orgnr, orgnr}}",
      velgHvemDuRepresenterer: "Choose who you want to represent",
      virksomheter: "Enterprises",
    },
    utloggetSide: {
      automatiskInfo:
        "You were silent for a while. To ensure your security, you were automatically logged out. The report has been saved, and you may continue to fill it out later",
      forsiden: "Go to the front page",
      logginn: "Log in again",
      tittel: "You have been logged out",
    },
  },
  erstatningskrav: {
    beskrivelseErstatningskrav: {
      helptext: "What is the loss? Why should this be replaced?",
      label: "Describe what compensation is required for",
    },
    dokumentasjonVedlegg: {
      label:
        "Upload documentation that the victim is entitled to compensation (optional)",
      label_ANMELDER:
        "Upload documentation that you are entitled to compensation (optional)",
      label_PERSON:
        "Upload documentation that the victim is entitled to compensation (optional)",
      label_VIRKSOMHET:
        "Upload documentation that the enterprise is entitled to compensation (optional)",
      helpText:
        "For example, receipts of what is lost or photos of what is damaged",
    },
    fullmaktVedlegg: {
      label_PERSON: `Upload power of attorney to file a compensation claim for the victim (optional)`,
      label_VIRKSOMHET: `Upload power of attorney to file a compensation claim for the enterprise (optional)`,
      helpText:
        "Legal guardians, owners, chairmen or similar do not need power of attorney",
    },
    infoErstatningskrav: {
      bulletpoints: [
        "If the case results in a fine or a court case, the police can claim compensation for items that have been stolen or damaged. The compensation goes to the person or business that has suffered the loss. If your loss is covered by the insurance company, it’s the insurance company that has the right to compensation.",
        "In order for us to claim compensation on your behalf, you must document and explain:",
        "BULLET_START",
        "What has been stolen or damaged",
        "The value of the items",
        "How the loss is related to the reported incident",
        "BULLET_END",
        "Documentation can, for example, be receipts, invoices, photos, or repair cost estimates.",
        "If you are reporting on behalf of another person or business, you must have a power of attorney showing that you have the right to claim compensation on behalf of the victim. A power of attorney should include information about who is giving it, who is receiving it, and what authority is being granted. It should be dated and signed.",
      ],
    },
    ja: "Yes",
    nei: "No",
    radio1: "Does the victim want to file a compensation claim?",
    radio1_ANMELDER: "Do you want to file a compensation claim?",
    radio1_PERSON: "Does the victim want to file a compensation claim?",
    radio1_VIRKSOMHET: "Does the enterprise want to file a compensation claim?",
    radioOptions: {
      JA: "Yes",
      NEI: "No",
      VET_IKKE: "I do not now",
    },
    se_over: {
      dokumentasjonVedlegg:
        "Documentation that the victim is entitled to compensation",
      dokumentasjonVedlegg_ANMELDER:
        "Documentation that you are entitled to compensation",
      dokumentasjonVedlegg_PERSON:
        "Documentation that the victim is entitled to compensation",
      dokumentasjonVedlegg_VIRKSOMHET:
        "Documentation that the enterprise is entitled to compensation",
      fullmaktVedlegg_PERSON:
        "Power of attorney to file a compensation claim for the victim",
      fullmaktVedlegg_VIRKSOMHET:
        "Power of attorney to file a compensation claim for the enterprise",
    },
    vet_ikke: "I do not now",
  },
  filtrering: {
    filtreringsType: {
      kategori: {
        options: {
          INNBRUDD: {
            label: "Burglary",
          },
          SKADEVERK: {
            label: "Damaged property",
          },
          TYVERI: {
            label: "Theft",
          },
          BEDRAGERI: {
            label: "Fraud",
          },
        },
        section: {
          label: "Category",
        },
      },
      tidsrom: {
        options: {
          FRA_DATO: {
            label: "From date",
          },
          TIL_DATO: {
            label: "To date",
          },
        },
        section: {
          label: "Time period",
        },
      },
    },
    mobilknapp: {
      label: "Filter",
      labelMedFilterOne: "{{antall}} active filters",
      labelMedFilterOther: "{{antall}} active filters",
    },
  },
  forside: {
    tittel: "Report digitally",
    spraakvelger: {
      ariaLabel: "Select language",
    },
    tabContainer: {
      anmelde: "Report",
      utkast: "Drafts",
      innsendt: "Submitted",
    },
    info: {
      title: "Information and guidance",
      tipsUtfylling: {
        title: "Important things to consider when filling out a report",
        bulletpoints: [
          "Please have documentation and attachments available before you start. Examples of documentation may include video surveillance, photos of stolen items, receipts, bank statements, or screenshots of communication between you and the perpetrator.",
          "The report consists of 8 steps, and you can fill it out in any order you wish. It may be helpful to go through all the steps before you begin so that you understand which information belongs where.",
          "A report can contain multiple types of offenses, as long as they are part of the same incident and has the same victim.",
          "In order for the police to be able to respond to the crime, it is important to have good descriptions of the incident and as much information as possible.",
          "Some information is important to fill in even if it is optional, such as serial numbers of stolen items and the personal identification numbers of people involved if you have it.",
          "If multiple perpetrators are involved in the same crime, only one report should be submitted. In these cases it is particularly important to provide a good description of what each individual has done and why they are suspected of the crime in question.",
        ],
        veileder: {
          lenke: "Read more in guide for digital crime reports",
        },
      },
      nyeOpplysningerOmAnmeldtForhold: {
        title: "New information about reported crime",
        tekst: [
          "If you would like to withdraw your report or provide us with more information about the case,",
          "please contact the police district where the incident occurred and provide the case number",
          "you were given when you submitted your report. We can then update or withdraw your report.",
          "You can also find the case numbers for all your online reports under “Submitted” on this page.",
        ],
        veileder: {
          lenke:
            "Read more about forwarding of documentation and find the right police station",
        },
      },
    },

    beskrivelse: {
      omAaAnmelde: {
        infotekst: {
          info: "In the reporting portal, you can report crimes like theft, vandalism, burglary, fraud and cybercrime digitally. You can also view your submitted reports or continue working on reports you have started to fill out.",
          andreKrimTyper:
            "If you wish to report other types of crime, you must contact your nearest police district.",
        },
      },
      dineAnmeldelser: {
        tittel: "Your reports",
        innsendteAnmeldelser: {
          tittel: "Submitted crime reports",
          badRequest:
            "Access control failed. It is not possible to view reports submitted on behalf of other people.",
          info: "Here are the crime reports that you have submitted on politiet.no. If you have questions about reports that were submitted in other ways, please contact your nearest police station.",
          infoRepresentasjon:
            "Here are the crime reports submitted on behalf of the enterprise on politiet.no. If you have questions about reports submitted in other ways, please contact your nearest police station.",
          infoTom:
            "There are no submitted crime reports here. If you are looking for a report you have previously submitted, please contact your nearest police station.",
          manglerTilgang:
            "You do not have access to view reports submitted on behalf of {{ valgtPartId }}. The general manager (daglig leder) should have access, and can also delegate access to others in Altinn.",
        },
        utkast: {
          tittel: "Your drafts",
          info: "Here are the crime reports that you have begun filling in but haven't submitted yet",
          tomt: "You have no drafts.",
        },
      },
    },
    knapper: {
      opprettAnmeldelse: {
        tyveriSkadeverkInnbrudd: "Report theft, burglary and damaged property",
        bedrageri: "Report fraud (scam) and cybercrime",
      },
      loggInn: "Log in",
    },
    eksisterendeAnmeldelseUtkast: {
      modal: {
        tittel: "Do you want to continue your draft?",
        beskrivelse:
          "You have already started a report for {{anmeldelseType}}. Do you want to continue?",
        fortsettUtfylling: "Continue filling out",
        opprettNy: "Create new report",
      },
    },
  },
  hvaHarSkjedd: {
    felles: {
      gjenstander: {
        forsikring: {
          title: "Insurance details",
          radioSpoersmaal: "Is the item to be replaced by insurance?",
          radioOptions: {
            JA: "Yes",
            NEI: "No",
          },
          polisenummer: {
            label: "Insurance number (policy number)",
          },
          forsikringsselskap: {
            se_over_label: "Insurance company",
          },
          sok: {
            label: "Insurance company",
            helpText:
              "Search for name or organization number. Check your insurance agreement if you are unsure who the provider is.",
            feilmeldingBrreg:
              "Unable to retrieve insurance companies from The Brønnøysund Register Centre",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For example, damage, color, sticker, accessories",
            label: "Special characteristics",
          },
          idnr: {
            helpText:
              "In order to to put out a bulletin for the vessel, we need an identifying number. Therefore, please fill in either the registration number or the hull number (WIN) if you wish to make the bulletin possible.",
            label: "Identifying number",
          },
          lengde: {
            label: "Lenght (feet)",
          },
          merke: {
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          nasjonalitet: {
            label: "Nationality",
          },
          pris: {
            helpText:
              "Enter the retail price or approximate price in whole kroner.",
            label: "Price (NOK)",
          },
          registreringsnummer: {
            helpText: "For example, serial number, sail number",
            label: "Registration number",
          },
          skrogtype: {
            ENKELT_SKROG: "Single hull",
            KATAMARAN: "Catamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Other type of boat",
            GUMMIBAAT: "Inflatable boat",
            JOLLE_ROBAAT: "Dinghy/Rowing boat",
            KANO_KAJAKK: "Canoe/Kayak",
            MOTORBAAT: "Motor boat",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Sailboat",
            SKOEYTE: "Fishing boat",
            SNEKKE: "Small open boat (snekke)",
            VANNSCOOTER: "Jet ski",
          },
          typeFartoy: "Select type of vessel",
          typeSkrog: "Hull type",
          win: {
            helpText: "Alternatively CIN or HIN",
            label: "WIN-code",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 digits)",
            label: "Production year",
          },
          farge: {
            label: "Colour",
            options: {
              "01": "White",
              "02": "Red",
              "03": "Blue",
              "04": "Green",
              "05": "Gray",
              "06": "Black",
              "07": "Beige",
              "08": "Brown",
              "09": "Yellow",
              "10": "Orange",
              "11": "Silver",
              "12": "Gold",
              "13": "Pink",
              "14": "Violet",
              "29": "Turquoise",
              "99": "Other",
            } satisfies Record<SVVFargekode, string>,
          },
          knapp: {
            manglerNorskRegNr: "Missing Norwegian registration number",
            manglerRegNr: "Missing registration number",
            norskRegNr: "Search for Norwegian registration number",
            regnr: "Registration number",
            slett: "Delete",
            utenlandskRegNr: "Foreign registration number",
          },
          merke: {
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          registreringsland: "Nationality",
          registreringsnummer: {
            feilmeldingSvv:
              "Could not contact the Norwegian Public Roads Administration, please try again later",
            label: "Add registration number (licence plate number)",
            helpText:
              "Enter the registration number and press enter or the magnifying glass to search",
            labelUtenlandsk: "Foreign registration number (license plate)",
            panelLabel: "Registration number (license plate number)",
            ugyldig:
              "The registration number must be between 2 and 7 characters",
          },
          type: {
            ANNET: "Other",
            BUSS: "Bus",
            BELTEBIL: "Tracked vehicle",
            CAMPINGBIL: "Campervan",
            CAMPINGVOGN: "Caravan",
            KOMBINERT_BIL: "Dual-purpose vehicle",
            LASTEBIL: "Truck",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorcycle",
            PERSONBIL: "Passenger car",
            SNOSCOOTER: "Snowmobile",
            TILHENGER: "Trailer",
            TRAKTOR: "Tractor",
            TREKKVOGN_ELLER_TREKKBIL: "Truck unit",
            VAREBIL: "Van",
          },
          typeKjoretoy: {
            label: "Select type of vehicle",
            seOverLabel: "Type of vehicle",
          },
          understellsnummer: {
            label: "Vehicle Identification Number (VIN)",
          },
        },
      },
      gjenstandTyper: {
        ANNET: "Other",
        FARTOY: "Boat, jet ski or other type of vessel",
        HANDELSVARER: "Item list",
        KJENNEMERKE: "License plate",
        KJOERETOEY: "Car, moped or other vehicles",
        MOBILTELEFON: "Phone",
        SYKKEL: "Bicycle or scooter",
        ELEKTRONIKK: "Electronics",
        BYGNING_EIENDOM: "Building or property",
        VERKTOY: "Tools",
      },
      modal: {
        fullfoer: "Complete",
        lukk: "Close",
      },
      panel: {
        redigerAriaLabel: "Edit item {{nr}}",
        gjenstandTypeIkkeValg: "Item",
        slett: "Delete",
        ukjentGjenstandType: "Item type is missing",
      },
    },
    bedrageri: {
      tittel: "Fraud",
      hvordanStartetBedrageriet: {
        title: "How did it start?",
        input: {
          initiellKontakt: {
            label:
              "How did you first come into contact with the scammer or scammers?",
            annet: {
              label: "Describe how you first came into contact",
            },
            options: {
              MELDING_SMS: "I received a message or SMS",
              E_POST: "I received an email",
              OPPRINGT: "I was called",
              KJOEP_SALG: "In an online marketplace",
              SOSIALE_MEDIER: "On social media",
              ANNET: "Other",
            },
          },
        },
      },
      hvaSkjedde: {
        title: "What happened?",
        options: {
          JA: "Yes",
          NEI: "No",
        },
        input: {
          beskrivelse: {
            label: "Describe the entire incident",
          },
          moettSvindlerFysisk: {
            label: "Did you physically meet the scammer or scammers?",
          },
          harDuMottattLenke: {
            label: "Did you receive a link related to with the scam?",
          },
          lenke: {
            label: "Paste the link here",
          },
          harInstallertProgramEllerApp: {
            label:
              "In relation to the scam, have you installed any software or applications on any of your devices?",
          },
          hvilketProgramEllerApp: {
            label: "What software or app did you install?",
          },
          paaHvilkenEnhet: {
            label: "On which device did you install the software or app?",
            options: {
              TELEFON: "Phone",
              PC: "PC",
              NETTBRETT: "Tablet",
            },
          },
        },
      },
      tilegnedeVerdier: {
        title: "What did you lose?",
        panel: {
          redigerAriaLabel: "Edit {{type}} {{nr}}",
        },
        options: {
          PENGER: "Money",
          PERSONINFORMASJON: "Personal information",
          KODE: "Code",
          PASSORD: "Password",
          FYSISK_ID: "Physical ID",
          ANNET: "Other",
        } satisfies Record<TilegnetVerdiType, string>,
        input: {
          tilegnetVerdi: {
            label: "What have the scammer or scammers obtained from you?",
            helpText: "Select all relevant alternatives",
          },
        },
        PENGER: {
          title: "Transaction",
          fjern: "Delete",
          modalTitle: "Description of transaction",
          info: "Select card payment if you have used a card to pay in fake online stores or other fake websites, or if there are withdrawals from your account with unknown origin.",
          knapper: {
            leggTil: "Add transaction",
            leggTilFlere: "Add another transaction",
          },
          fratattSum: {
            label: "How much did the scammer or scammers obtain?",
            seOverLabel: "Total value",
            helpText: "Enter the amount in whole NOK",
            totalSum: "Total amount of stolen money (NOK)",
          },
          overforing: {
            dropdown: {
              label: "How did the scammer or scammers obtain the money?",
            },
            options: {
              KONTOOVERFORING: "Account transfer",
              VIPPS: "Vipps transfer",
              KRYPTO: "Cryptotransaction",
              KORTBETALING: "Card payment",
              ANNET: "Other",
            },
            KONTOOVERFORING: {
              fraKontonummer: {
                label: "From which account was the money transferred?",
                seOverLabel: "From account",
                helpText: "Enter your account number, IBAN or BIC",
              },
              tilKontonummer: {
                label:
                  "To which account was the money transferred?  (optional)",
                seOverLabel: "To account",
                helpText: "Enter your account number, IBAN or BIC",
              },
              fraBank: {
                label: "In which bank is this account?",
                seOverLabel: "Bank",
              },
            },
            KORTBETALING: {
              kortnummer: {
                infoboks:
                  "Card number, and account number is important information for further investigation. It is therefore important that you submit these if you have them.",
                label: "Which card was used in the payment? (optional)",
                seOverLabel: "Card number",
                helpText: "Enter card number",
              },
              fraKontonummer: {
                label:
                  "From which account was the money transferred? (optional)",
                seOverLabel: "From account",
                helpText: "Enter account number, IBAN or BIC",
              },
              fraBank: {
                label: "In which bank is this account?",
                seOverLabel: "Bank",
              },
              mottaker: {
                label: "To which recipient was the money paid?",
                seOverLabel: "To recipient",
                helpText: "For example, name of person or business",
              },
            },
            VIPPSOVERFORING: {
              fraKontonummer: {
                label:
                  "From which account was the money transferred? (optional)",
                seOverLabel: "From account",
                helpText: "Enter your account number, IBAN or BIC",
              },
              fraTelefonnummer: {
                label: "From which phone number was the money transferred?",
                seOverLabel: "From phone number",
              },
              tilTelefonnummer: {
                label:
                  "To which phone number or Vipps number was the money transferred?",
                seOverLabel: "To phone number / Vipps number",
              },
              fraBank: {
                label: "In which bank is this account?",
                seOverLabel: "Bank",
              },
              mottakernavn: {
                label: "Recipient name",
                seOverLabel: "Recipient",
              },
            },
            KRYPTO: {
              belop: {
                label: "What was the amount in cryptocurrency?",
                helpText: "Enter the amount in the applicable cryptocurrency",
              },
              valuta: {
                label: "Enter cryptocurrency",
                helpText: "For example Bitcoin, Ethereum, XRP",
              },
              fraAdresse: {
                label:
                  "From which cryptocurrency address was the money transferred?",
                seOverLabel: "From cryptocurrency address",
                helpText: "From cryptocurrency address",
              },
              tilAdresse: {
                label:
                  "Which cryptocurrency address did the money go to? (optional)",
                seOverLabel: "To cryptocurrency address",
              },
              vekslingstjeneste: {
                label: "Which exchange service was used?",
                helpText: "What cryptocurrency exchange was used?",
                seOverLabel: "Currency exchange service",
              },
              transaksjonsId: {
                label: "Transaction ID (hash) of the transfer",
                seOverLabel: "Transaction ID",
              },
            },
            ANNET: {
              beskrivelse: {
                label: "Describe how the money was taken and where it went",
                seOverLabel: "Description",
                helpText:
                  "For example, withdrawals at a named ATM or use in a named store",
              },
            },
          },
          selvOverfort: {
            harDuSelvOverfortPengene: {
              options: {
                JA: "Yes",
                NEI: "No",
              },
              label:
                "Did you transfer the money to the scammer or scammers yourself?",
            },
            overforingFormaal: {
              label: "What was the purpose of the transfer?",
              options: {
                INVESTERING: "Investment",
                VARER_TJENESTER: "Purchase of goods or services",
                GAVE: "A gift",
                UTLAAN: "Lending",
                FAKTURA: "Invoice (fake)",
                UTPRESSING: "Blackmail",
                ABONNEMENT: "Subscription",
                GEBYR: "Paying a fee",
                ANNET: "Other",
              },
            },
            annetOverforingFormaalBeskrivelse: {
              label: "Explain what the purpose of the transfer was",
            },
            erOverforingGodkjentMedPassordEtc: {
              label:
                "Have you approved transfers by entering a code or password?",
              options: {
                JA: "Yes",
                NEI: "No",
              },
            },
          },
          tilbakeforing: {
            type: {
              label: "Have you received the money back?",
              options: {
                JA: "Yes",
                NEI: "No",
                DELVIS: "Partially",
              },
            },
            hvemHarFornaermedeFaattPengerFra: {
              label: "Who did you get the money back from?",
            },
            hvorMyeHarFornaermedeFaattTilbake: {
              label: "How much have you received back?",
              helpText: "Enter the amount in NOK",
            },
          },
          bankenStansetOverforing: {
            overforingStanset: {
              label: "Has the bank stopped the transaction?",
            },
            hvorMyeHarBankenStanset: {
              label: "How much has the bank stopped?",
              helpText: "Enter the amount in NOK",
            },
            optionsJaNeiDelvis: {
              JA: "Yes",
              NEI: "No",
              DELVIS: "Partially",
            } satisfies Record<HarBankenStansetOverforingType, string>,
          },
        },
        PERSONINFORMASJON: {
          checkbox: {
            label:
              "What personal information has the scammer or scammers obtained?",
            options: {
              NAVN: "Name",
              ADRESSE: "Address",
              TELEFONNUMMER: "Phone number",
              FOEDSELSNUMMER_ELLER_DNUMMER:
                "National identity number / D number",
              ANNET: "Other personal information",
            },
            extraInput: {
              ANNET: {
                label: "Explain which personal data",
              },
            },
          },
        },
        KODE: {
          beskrivelse: {
            label: "What was the code for?",
          },
        },
        PASSORD: {
          beskrivelse: {
            label: "What was the password for?",
          },
        },
        FYSISK_ID: {
          checkbox: {
            label:
              "What kind of physical ID has the scammer or scammers obtained?",
            options: {
              PASS: "Passport",
              NASJONALT_ID_KORT: "National ID card",
              FORERKORT: "Driving license",
              BANKKORT_MED_BILDE: "Bank card with photo",
              ANNET: "Other",
            },
            extraInput: {
              ANNET: {
                label: "Explain what kind of physical ID",
              },
            },
          },
        },
        ANNET: {
          beskrivelse: {
            label: "Describe what the scammer has taken",
            helpText: "What is it? Has it been returned? Quantity?",
          },
          sum: {
            label: "Value of what was taken (NOK)",
            helpText:
              "Enter the retail price or approximate price in whole kroner.",
          },
        },
      },
      hvaSkjeddeEtter: {
        title: "What happened next?",
        optionsJaNei: {
          JA: "Yes",
          NEI: "No",
        },
        input: {
          vetBanken: {
            label: "Does your bank know what happened?",
          },
          kortSperret: {
            label: "Have your card been blocked?",
          },
          kontoSperret: {
            label: "Have your account been blocked?",
          },
        },
      },
    },
    innbrudd: {
      beskrivelse: {
        helpText: "For example shed, car, warehouse",
        label: "Where was the burglary?",
      },
      se_over: {
        beskrivelse: "Description",
        label: "Where was the burglary?",
      },
      tittel: "Burglary",
    },
    options: {
      INNBRUDD: { label: "Someone has broken in somewhere (burglary)" },
      SKADEVERK: { label: "Someone has damaged something (damaged property)" },
      TYVERI: { label: "Someone has stolen something (theft)" },
      BEDRAGERI: {
        label: "Fraud (scam)",
        helpText:
          "Someone has fraudulently obtained or attempted to obtain money or assets to which they were not entitled",
      },
      DATAKRIM: {
        label: "Cybercrime",
        helpText:
          "Someone has unlawfully gained access to a computer system and stolen, altered, or destroyed data.",
      },
    },
    datakrim: {
      tittel: "Cybercrime",
      infoboks_sikkert_dot_no:
        "On <anchorlink>sikkert.no</anchorlink>, you can find more advice from the authorities on handling an unwanted digital incident, or preventing it from happening in the future.",
      datakrimType: {
        checkbox: {
          label: "What best describes what happened",
          helpText:
            "Tick all options that apply (multiple selections possible).",
          extraInput: {
            ANNET: {
              label: "Briefly describe what happened",
              se_over: "Briefly describe what happened (other)",
            },
          },
          options: {
            DATAINNBRUDD: {
              label: "Data breach",
              helpText:
                "someone has gained unauthorized access to a computer system",
            },
            VIRUS: {
              label: "Ransomware or malware",
              helpText:
                "software that has locked or encrypted content within a computer system",
            },
            DOS_DDOS: {
              label: "Denial-of-service attack (DoS)",
              helpText:
                "An attack that makes information, resources, or services completely or partially unavailable",
            },
            KJOP_SALG_INNLOGGINGSINFORMASJON: {
              label: "Buying and selling of login credentials",
            },
            ID_TYVERI: { label: "Identity theft or account takeover" },
            ANNET: { label: "Other" },
          } satisfies Record<
            DatakrimType,
            { label: string; helpText?: string }
          >,
        },
      },
      dataPaAvveie: {
        label: "Is there reason to believe that data has been compromised?",
        beskrivelse: {
          label: "Explain why",
        },
      },
      andreMyndigheterVarslet: {
        label:
          "Have other authorities or organizations been notified or involved in handling this incident?",
        beskrivelse: {
          label: "Which authorities or organizations are involved?",
        },
      },
      datakrimSvarType: {
        JA: "Yes",
        NEI: "No",
      } satisfies Record<DatakrimSvarType, string>,
      tilleggsinformasjon: {
        label: "Information about the incident or those responsible (optional)",
        helpText:
          "For example, IP addresses, domains, hashes, file names, email addresses, cryptocurrency addresses, or names.",
      },
    },
    skadeverk: {
      beskrivelse: {
        helpText: "For example what is damaged or how it happened",
        label: "Describe the damage",
      },
      formuestap: {
        helpText: "If you are unsure, you can provide an approximate price",
        label: "Price of what is damaged (NOK)",
      },
      gjenstander: {
        bygningEiendom: {
          beskrivelse: {
            helpText:
              "What part of the property is damaged. How was it damaged?",
            label: "Describe what is damaged",
          },
          panel: {
            beskrivelse: {
              label: "Description",
            },
            pris: "Price",
          },
        },
        annenGjenstand: {
          beskrivelse: {
            helpText: "What is it? How was it damaged?",
            label: "Describe what is damaged",
          },
          panel: {
            beskrivelse: {
              label: "Description",
            },
            pris: "Price",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For example, damage, color, sticker, accessories",
            label: "Special characteristics",
          },
          idnr: {
            helpText:
              "In order to to put out a bulletin for the vessel, we need an identifying number. Therefore, please fill in either the registration number or the hull number (WIN) if you wish to make the bulletin possible.",
            label: "Identifying number",
          },
          lengde: {
            label: "Lenght (feet)",
          },
          merke: {
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          nasjonalitet: {
            label: "Nationality",
          },
          pris: {
            helpText:
              "Enter the retail price or approximate price in whole kroner.",
            label: "Price (NOK)",
          },
          registreringsnummer: {
            helpText: "For example, serial number, sail number",
            label: "Registration number",
          },
          skrogtype: {
            ENKELT_SKROG: "Single hull",
            KATAMARAN: "Catamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Other type of boat",
            GUMMIBAAT: "Inflatable boat",
            JOLLE_ROBAAT: "Dinghy/Rowing boat",
            KANO_KAJAKK: "Canoe/Kayak",
            MOTORBAAT: "Motor boat",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Sailboat",
            SKOEYTE: "Fishing boat",
            SNEKKE: "Small open boat (snekke)",
            VANNSCOOTER: "Jet ski",
          },
          typeFartoy: "Type of vessel",
          typeLabel: "Type of vessel",
          typeSkrog: "Hull type",
          win: {
            helpText: "Alternatively CIN or HIN",
            label: "WIN-code",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 digits)",
            label: "Production year",
          },
          farge: {
            label: "Colour",
          },
          knapp: {
            manglerNorskRegNr: "Missing Norwegian registration number",
            manglerRegNr: "Missing registration number",
            norskRegNr: "Search for Norwegian registration number",
            slett: "Delete",
            utenlandskRegNr: "Foreign registration number",
          },
          merke: {
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          registreringsland: "Nationality",
          registreringsnummer: {
            feilmeldingSvv:
              "Could not contact the Norwegian Public Roads Administration, please try again later",
            label: "Add registration number (licence plate number)",
            helpText:
              "Enter the registration number and press enter or the magnifying glass to search",
            labelUtenlandsk: "Foreign registration number (license plate)",
            panelLabel: "Registration number (license plate number)",
            ugyldig:
              "The registration number must be between 2 and 7 characters",
          },
          type: {
            ANNET: "Other",
            BUSS: "Bus",
            BELTEBIL: "Tracked vehicle",
            CAMPINGBIL: "Campervan",
            CAMPINGVOGN: "Caravan",
            KOMBINERT_BIL: "Dual-purpose vehicle",
            LASTEBIL: "Truck",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorcycle",
            PERSONBIL: "Passenger car",
            SNOSCOOTER: "Snowmobile",
            TILHENGER: "Trailer",
            TRAKTOR: "Tractor",
            TREKKVOGN_ELLER_TREKKBIL: "Truck unit",
            VAREBIL: "Van",
          },
          typeKjoretoy: {
            label: "Select type of vehicle",
          },
          understellsnummer: {
            label: "Vehicle Identification Number (VIN)",
          },
        },
        sum: "Total value of damaged property (NOK)",
      },
      leggTilFlereGjenstander: "Add another damaged item",
      leggTilGjenstand: "Add damaged item",
      modal: {
        dropdownLabel: "What is damaged?",
        tittel: "Description of damaged item",
      },
      panel: {
        beskrivelse: {
          label: "Damage description",
        },
        dropdownLabel: "What is damaged?",
        formuestap: {
          label: "Price",
        },
      },
      tittel: "Damaged property",
    },
    tyveri: {
      beskrivelse: {
        helpText: "For example, damage, color, sticker, accessories",
        label: "Special characteristics",
      },
      gjenstander: {
        annenGjenstand: {
          beskrivelse: {
            helpText: "What is it? Has it been returned? Quantity?",
            label: "Describe what is stolen",
          },
          panel: {
            beskrivelse: {
              label: "Description",
            },
            pris: "Price",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For example, damage, color, sticker, accessories",
            label: "Special characteristics",
          },
          idnr: {
            helpText:
              "In order to to put out a bulletin for the vessel, we need an identifying number. Therefore, please fill in either the registration number or the hull number (WIN) if you wish to make the bulletin possible.",
            label: "Identifying number",
          },
          lengde: {
            label: "Lenght (feet)",
          },
          merke: {
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          nasjonalitet: {
            label: "Nationality",
          },
          pris: {
            helpText:
              "Enter the retail price or approximate price in whole kroner.",
            label: "Price (NOK)",
          },
          registreringsnummer: {
            helpText: "For example, serial number, sail number",
            label: "Registration number",
          },
          skrogtype: {
            ENKELT_SKROG: "Single hull",
            KATAMARAN: "Catamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Other type of boat",
            GUMMIBAAT: "Inflatable boat",
            JOLLE_ROBAAT: "Dinghy/Rowing boat",
            KANO_KAJAKK: "Canoe/Kayak",
            MOTORBAAT: "Motor boat",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Sailboat",
            SKOEYTE: "Fishing boat",
            SNEKKE: "Small open boat (snekke)",
            VANNSCOOTER: "Jet ski",
          },
          typeFartoy: "Type of vessel",
          typeLabel: "Type of vessel",
          typeSkrog: "Hull type",
          win: {
            helpText: "Alternatively CIN or HIN",
            label: "WIN-code",
          },
        },
        handelsvarer: {
          caption: {
            label: "Item list",
          },
          antall: {
            label: "Amount",
          },
          leggTilFlereVarer: "Add another item",
          leggTilVare: "Add item",
          slett: {
            ariaLabel: "Delete item",
          },
          navn: {
            label: "Item type and brand",
          },
          pris: {
            label: "Price per piece (NOK)",
          },
          total: {
            label: "Total value (NOK)",
          },
        },
        kjennemerke: {
          kategori: {
            label: "Registration number category",
            options: {
              KJORETOY: "Main license plate",
              NORMAL: "Standard",
              PERSONLIG: "Personalised number plate",
              PROVE: "Temporary license plate",
            } satisfies Record<KjennemerkeKategori, string>,
          },
          type: {
            label: "Number plate type",
            options: {
              AMBASSADE:
                "Embassy number plates (Yellow characters on blue background)",
              ARSPROVEKJENNEMERKE:
                "Annual temporary number plate (White characters on red background)",
              ARSPROVEKJENNEMERKE_T:
                "Annual temporary number plate Trailer (White characters on red background with T)",
              DAGSPROVEKJENNEMERKE:
                "Day temporary number plate (Black characters on red background)",
              ORDINART:
                "Ordinary number plates (Black characters on white background)",
              RALLY_TRIAL_ENDURO:
                "Rally number plates (White characters on black background)",
              SVALBARD:
                "Svalbard number plates (Yellow characters on black background)",
              UTENFOR_OFFENTLIG_VEG:
                "Off public road (Yellow characters on black background)",
              VAREBIL_KLASSE_2:
                "Van class 2 (Black characters on green background)",
              VETERAN: "Veteran number plates",
              VETERAN_UTENFOR_OFFENTLIG_VEI:
                "Veteran off public road (Yellow characters on black background)",
            } satisfies Record<KjennemerketypeType, string>,
          },
          antallSkilt: {
            label: "How many license plates have been stolen?",
          },
          panel: {
            antallSkilt: "Amount of stolen licence plates",
            registreringsnummer: "Registration number (license plate number)",
          },
          registreringsnummer: {
            label: "Add registration number (licence plate number)",
            helpText:
              "Enter the registration number and press enter or the magnifying glass to search",
          },
          alert: {
            header: "Is the number plate lost?",
            body: "In that case, it should not be reported as stolen. Instead, you must fill out a <0>form about loss of number plates</0>. The completed form must be signed and stamped at a police station",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 digits)",
            label: "Production year",
          },
          farge: {
            label: "Colour",
          },
          knapp: {
            manglerNorskRegNr: "Missing Norwegian registration number",
            manglerRegNr: "Missing registration number",
            norskRegNr: "Search for Norwegian registration number",
            slett: "Delete",
            utenlandskRegNr: "Foreign registration number",
          },
          merke: {
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          registreringsland: "Nationality",
          registreringsnummer: {
            feilmeldingSvv:
              "Could not contact the Norwegian Public Roads Administration, please try again later",
            label: "Add registration number (licence plate number)",
            helpText:
              "Enter the registration number and press enter or the magnifying glass to search",
            labelUtenlandsk: "Foreign registration number (license plate)",
            panelLabel: "Registration number (license plate number)",
            ugyldig:
              "The registration number must be between 2 and 7 characters",
          },
          type: {
            ANNET: "Other",
            BUSS: "Bus",
            BELTEBIL: "Tracked vehicle",
            CAMPINGBIL: "Campervan",
            CAMPINGVOGN: "Caravan",
            KOMBINERT_BIL: "Dual-purpose vehicle",
            LASTEBIL: "Truck",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorcycle",
            PERSONBIL: "Passenger car",
            SNOSCOOTER: "Snowmobile",
            TILHENGER: "Trailer",
            TRAKTOR: "Tractor",
            TREKKVOGN_ELLER_TREKKBIL: "Truck unit",
            VAREBIL: "Van",
          },
          typeKjoretoy: {
            label: "Type of vehicle",
          },
          understellsnummer: {
            label: "Vehicle Identification Number (VIN)",
          },
        },
        mobiltelefon: {
          imei: {
            label: "IMEI number",
            helpText: "15 digits",
          },
          merke: {
            helpText: "For example Apple, Samsung",
            label: "Brand",
          },
          modell: {
            helpText: "For example iPhone 14 Pro, Galaxy A25",
            label: "Model",
          },
          pris: {
            label: "Price",
          },
          telefonnummer: {
            label: "Phone number",
          },
          teleselskap: {
            helpText: "For example, Telenor, Telia",
            label: "Telecom company",
          },
        },
        sykkel: {
          merke: {
            helpText: "For example DBS, Merida, Scott",
            label: "Brand",
          },
          modell: {
            label: "Model",
          },
          rammenummer: {
            helpText: "Please provide frame number if you have it",
            label: "Frame number/serial number",
          },
          type: {
            ANNEN_TYPE_SYKKEL: "Other type of bicycle",
            BARNESYKKEL: "Children's bike",
            BYSYKKEL: "City bike",
            CYCLOCROSS_GRAVEL: "Cyclocross / Gravel",
            EL_SPARKESYKKEL: "Electric scooter",
            EL_SYKKEL: "Electric bicycle",
            HYBRIDSYKKEL: "Hybrid bike",
            LANDEVEISSYKKEL: "Road bike",
            SPARKESYKKEL: "Scooter",
            TERRENGSYKKEL: "Mountain bike",
          },
          typeSykkel: "Select type of bicycle",
        },
        elektronikk: {
          type: {
            NETTBRETT: "Tablet",
            DATAMASKIN: "Computer",
            SPILLKONSOLL: "Game console",
            SMARTKLOKKE: "Smartwatch",
            TV: "TV",
            HODETELEFONER: "Headphones",
            HVITEVARER: "Appliances",
            STØVSUGER: "Vacuum cleaner",
            ANNET: "Other",
          },
          typeElektronikk: "Select type of electronics",
          merke: {
            label: "Brand",
            helpText: "For example Samsung, Playstation, Apple",
          },
          modell: {
            label: "Model",
            helpText: "For example Galaxy buds, PS5, MacBook Pro",
          },
          serienummer: {
            label: "Serial number (optional)",
          },
          pris: {
            label: "Price",
          },
        },
        verktoy: {
          type: {
            label: "Type of tool",
            helpText: "For example a drill, saw, or angle grinder",
          },
          merke: {
            label: "Brand",
            helpText: "For example Bosch, Ryobi, DeWalt",
          },
          modell: {
            label: "Model",
          },
          serienummer: {
            label: "Serial number (optional)",
          },
        },
        sum: "Total price of stolen items (NOK)",
      },
      leggTilFlereGjenstander: "Add another stolen item",
      leggTilGjenstand: "Add stolen item",
      modal: {
        dropdownLabel: "What is stolen?",
        tittel: "Description of stolen item",
      },
      panel: {
        beskrivelse: {
          label: "Special characteristics",
        },
        dropdownLabel: "What is stolen?",
        pris: {
          label: "Price",
        },
      },
      pris: {
        helpText:
          "Enter the retail price or approximate price in whole kroner.",
        label: "Price of what is stolen (NOK)",
      },
      tittel: "Theft",
    },
  },
  hvemGjordeDet: {
    navbar: "Perpetrator steps",
    gjerningspersoninformasjonSvarType: {
      KJENT: "Yes",
      UKJENT: "No, I don't know who it is",
    },
    modal: {
      tittel: "About the perpetrator",
      forrige: "Previous",
      neste: "Next",
      fullfoer: "Complete",
      lukk: "Close",
      avbryt: "Abort",
      fornavn: "Enter first name",
      etternavn: "Enter last name",
      epost: "Email address",
      telefon: "Phone number",
      beskrivelse: "Other information",
    },
    panel: {
      antattGjerningsperson: "Presumed perpetrator",
      gjerningspersonNr: "Perpetrator {{nr}}",
      slett: "Delete perpetrator",
      redigerAriaLabel: "Edit perpetrator {{nr}}",
    },
    gjerningsperson: {
      details: {
        gjerningspersonNr: "Perpetrator {{nr}}",
        label: "Add one or more types of information",
        alder: {
          gjerningspersonAlderSvarType: {
            OVER_ATTEN: "Over 18 years old",
            UNDER_ATTEN: "Under 18 years old",
            UNDER_FEMTEN: "Under 15 years old",
            VET_IKKE: "I do not now",
          },
          label: "How old is the perpetrator?",
          title: "Age",
          vergeInfo:
            "The person you added is under 18 years old. If you have information about a legal guardian or representative, you can add it using a separate button.",
        },
        begrunnelseForInvolvering: {
          label: "Why do you think this person did it?",
        },
        slett: "Delete perpetrator",
      },
      panel: {
        endre: {
          label: "Edit",
        },
        slett: {
          label: "Delete",
        },
      },
      fragments: {
        personopplysninger: {
          leggTil: "Add name or other personal information",
          tittel: "Personal information",
          helptext:
            "You must provide <strong>at least one</strong> personal detail, but preferably as many as you can",
          fornavn: "First name",
          etternavn: "Surname",
          foedselsEllerDnummer: {
            label: "Personal ID (11 digits)",
            helpText: "National identity number / D number",
          },
          foedselsdato: {
            label: "Date of birth",
            helpText: "dd.mm.yyyy",
          },
          identifiseringsinformasjon: {
            beskrivelse: {
              ANNET: {
                label: "Describe how you identified the person",
              },
              FREMVIST_ID: {
                label: "What kind of ID has been presented?",
              },
            },
            label: "How is the person identified?",
            options: {
              ANNET: "Other",
              FREMVIST_ID: "Shown ID",
              IKKE_IDENTIFISERT: "The person has not been identified.",
            },
          },
          vergeInfo:
            "The person you added is under 18 years old. If you have information about a legal guardian or representative, you can add it using a separate button.",
        },
        beskrivelse: {
          leggTil: "Add a description of the person",
          tittel: "Description",
          label: "Describe the perpetrator as well as you can",
          helptext:
            "For example gender, hair color and length, estimated age, height, clothes, language, scars, etc.",
        },
        verge: {
          leggTil: "Add information about guardian or representative",
          tittel: "Information about guardian or representative",

          personnavn: {
            etternavn: {
              label: "Surname",
            },
            fornavn: {
              label: "First name",
            },
          },
          kontaktopplysninger: {
            telefon: {
              label: "Phone number",
            },
          },
          vergeKontaktet: {
            tittel: "Have you contacted the legal guardian?",
            vergeErKontaktetSvarType: {
              JA: "Yes",
              NEI: "No",
            },
          },
          vergeTilStede: {
            tittel: "Did the legal guardian arrive at the location?",
            vergeTilStedeSvarType: {
              JA: "Yes",
              NEI: "No",
            },
          },
          panel: {
            personnavn: {
              fornavn: "Guardian's first name",
              etternavn: "Guardian's surname",
            },
            kontaktopplysninger: {
              telefon: "Guardian's phone number",
            },
          },
        },
        oppgitteOpplysninger: {
          leggTil: "Add provided name and/or organization",
          tittel: "Provided name and/or organization",
          helptext:
            "You must fill in <strong>at least one</strong> field, but preferably as many as you can",
          personnavn: {
            fornavn: "What first name did the person provide?",
            etternavn: "What surname did the person provide?",
          },
          virksomhetNavn:
            "What organization or enterprise did the person say they represented?",
          andreOpplysninger: "Other information about the stated identity",
          panel: {
            personnavn: {
              fornavn: "Provided first name",
              etternavn: "Provided surname",
            },
            virksomhetNavn: "Organization",
            andreOpplysninger: "Other information",
          },
        },
        kontaktopplysninger: {
          leggTil: "Add contact information",
          tittel: "Contact information",
          slett: "Delete",
          label: "What contact information do you have?",
          helptext: "Check one or more options that apply",
          options: {
            TELEFONNUMMER: {
              label: "Phone number",
              leggTil: "Add phone number",
            },
            EPOST: {
              label: "Email address",
              leggTil: "Add email address",
            },
            BRUKERNAVN: {
              label: "Username",
              leggTil: "Add username",
              context: "Where was the username used?",
              contextHelptext: "For example Finn.no, Whatsapp",
            },
            ADRESSE: {
              label: "Address",
              leggTil: "Add address",
            },
            ANNET: {
              label: "Other",
              leggTil: "Add other",
            },
          },
          panel: {
            brukernavn: {
              context: "Where was the username used?",
            },
          },
        },
      },
    },
    gjerningsvirksomhet: {
      panel: {
        tittel: "Enterprise",
        navnMangler: "(name is missing)",
      },
      sokefelt: {
        label: "Search the Central Coordinating Register",
        helpText: "Search by name or organization number",
        feilmeldingBrreg:
          "Could not connect to the Central Coordinating Register, please try again later",
      },
      leggTilManuelt: "Add enterprise manually",
      valgtVirksomhet: {
        opplysninger: "Information from Central Coordinating Register",
        manuelleOpplysninger: "Information added manually",
        endre: "Edit",
      },
      manuellVirksomhet: {
        modalTittel: "Add enterprise manually",
        virksomhetensNavn: {
          label: "Name of the enterprise",
        },
        informasjon: {
          label:
            "What information do you have about the enterprise? (optional)",
          helpText: "Select one or more applicable options",
        },
        informasjonstyper: {
          NETTSTED: "Website",
          E_POST: "Email",
          TELEFON: "Phone number",
          ADRESSE: "Address",
          ORGNR: "Organization number",
          ANNET: "Other",
        },
        input: {
          NETTSTED: "Website",
          E_POST: "Email address",
          TELEFON: "Phone number",
          ADRESSE: "Address",
          ORGNR: "Organization number",
          ANNET: "Other information",
        },
        fullfoer: "Complete",
        avbryt: "Cancel",
      },
      hvorforSkyldig: {
        label: "Why do you believe this enterprise is responsible?",
      },
      slett: "Delete enterprise",
    },
    radioKnapper: {
      fritekst: "Yes, I can give a description",
      kjent: "Yes",
      tittel:
        "Do you have any information about the person or persons you think did it?",
      helpText: "You can add multiple perpetrators",
      ukjent: "No, I don't know who it is",
    },
    steg: {
      alder: {
        gjerningspersonAlderSvarType: {
          OVER_ATTEN: "Over 18 years old",
          UNDER_ATTEN: "Under 18 years old",
          UNDER_FEMTEN: "Under 15 years old",
          VET_IKKE: "I do not now",
        },
        label: "How old is the perpetrator?",
        title: "Age",
      },
      begrunnelse: {
        begrunnelseForInvolvering: {
          label: "Why do you think this person did it?",
        },
        identifiseringsinformasjon: {
          beskrivelse: {
            ANNET: {
              label: "Describe how you identified the person",
            },
            FREMVIST_ID: {
              label: "What kind of ID has been presented?",
            },
          },
          label: "How is the person identified?",
          options: {
            ANNET: "Other",
            FREMVIST_ID: "Shown ID",
            IKKE_IDENTIFISERT: "The person has not been identified.",
          },
        },
        title: "Justification",
      },
      personalia: {
        beskrivelse: "Other information about the perpetrator",
        beskrivelseHelpText:
          "For example gender, hair color, age, height, shape, clothing, language, dialect, scars, etc.",
        beskrivelseHelpTextBedrageri:
          "For example other usernames, language/dialect, stated affiliation/organization",
        title: "Personal information",
      },
      verge: {
        harKontaktinformasjonTilVerge: {
          title: "Do you have contact information for the legal guardian?",
        },
        harVergesKontaktinformasjonSvarType: {
          JA: "Yes",
          NEI: "No",
        },
        kontaktopplysninger: {
          telefon: {
            label: "Phone number",
          },
        },
        label: "Contact information for legal guardian",
        personnavn: {
          etternavn: {
            label: "Surname",
          },
          fornavn: {
            label: "First name",
          },
        },
        title: "Legal guardian",
        vergeKontaktet: {
          tittel: "Have you contacted the legal guardian?",
          vergeErKontaktetSvarType: {
            JA: "Yes",
            NEI: "No",
          },
        },
        vergeTilStede: {
          tittel: "Did the legal guardian arrive at the location?",
          vergeTilStedeSvarType: {
            JA: "Yes",
            NEI: "No",
          },
        },
      },
    },
    input: {
      beskrivelse: "Other information about the perpetrator",
      beskrivelseHelpText:
        "For example gender, hair color, age, height, shape, clothing, language, dialect, scars, etc.",
      fornavn: "First name",
      etternavn: "Surname",
      telefon: "Phone number",
      vergeLabel: "Contact information for legal guardian",
    },
    leggTilGjerningsvirksomhet: "Add enterprise",
    leggTilFlereGjerningsperson: "Add another perpetrator",
    leggTilGjerningsperson: "Add perpetrator",
  },
  hvemSaaDet: {
    anmelderErVitne: "You wil be registered as a witness in the case",
    infoboks:
      "Witnesses are important in shedding light on what happened. In case we need to gather more information, we will need the contact information of any witnesses.",
    input: {
      beskrivelse: "Other information about the witness",
      beskrivelseHelpText:
        "For example, role, employment relationship, what the witness has observed",
    },
    ja: "Yes",
    leggTilFlereVitner: "Add another witness",
    leggTilVitne: "Add witness",
    modal: {
      fullfoer: "Complete",
      lukk: "Close",
      tittel: "About the witness",
    },
    nei: "No",
    panel: {
      redigerAriaLabel: "Edit witness {{nr}}",
      slett: "Delete",
      title: "Witness",
    },
    saaAndre:
      "Do you know of any other witnesses who saw what happened or have information about the incident?",
    saaDu: "Did you see the incident happen?",
    vitne: "Witness",
    vitneInformasjonOptions: {
      JA: "Yes",
      NEI: "No",
    },
  },
  hvordanSkjeddeDet: {
    tittel: "How dit it happen?",
    infoboksTitle: "Help us understand how it happened",
    TYVERI_SKADEVERK_INNBRUDD: {
      infoboks: {
        beskriv: "Describe as well as you can what happened",
        bulletpoints: [
          "What happened before, during and after the incident",
          "Information about the perpetrator's method of action",
          "Were you in contact with the person or persons you believe to be the perpetrator?",
          "Why do you think the perpetrator intended to steal?",
          "How did the perpetrator respond to the accusation?",
          "Was the perpetrator violent or threatening?",
        ],
        hjelpetekst:
          "What is relevant information varies from event to event, and every detail can be important. Typical things that are useful for us to know are:",
      },
      inputLabel: "Explain how it happened",
    },
    BEDRAGERI: {
      infoboks: {
        infotekst:
          "Your description should be as detailed as possible. Details are important. Feel free to write the text chronologically and explain relevant history.",
        bulletInfo: "Typical things that are useful for us to know are:",
        bulletpoints: [
          "What happened before, during and after the incident",
          "Have you recieved any inquiries for your personal information prior to this incident?",
          "Date of specific events if it has happened over time",
          "What did the perpetrator or perpetrators want you to do?",
          "How did you communicate with the perpetrator or perpetrators?",
        ],
        bunnInfo:
          "You have the right not to provide information that may contribute to the prosecution or conviction of a close family member or another person with whom you have a close relationship. You also have the right not to provide information where doing so would constitute a breach of your duty of confidentiality.",
      },
      inputLabel: "Explain how it happened",
    },
  },
  hvorOgNaar: {
    gjerningssted: {
      radioOptions: {
        ANNEN_ADRESSE: "Search by address or location",
        IKKE_FYSISK_STED:
          "The incident cannot be linked to a physical location",
        UTENLANDSK_ADRESSE: "Abroad",
        KARTPUNKT: "Select point on map",
      },
      annenAdresseEllerSted: {
        annenRadio: "Search by another address or location",
        label: "Search for and select an address or place",
        slett: "Delete",
        oppslag: {
          forLang: "Invalid search, max 100 characters",
          ulovligeTegn:
            'Avoid special characters in search : ; ! # % \\ ( ) { } [ ] "',
        },
        kartverketFeil:
          "Could not contact the Norwegian Mapping Authority, please try again later",
        adresse: {
          bruksenhetsnummer: {
            label: "Select unit number (optional)",
            panelLabel: "Select unit number",
          },
        },
        sted: {
          beskrivelse: {
            label: "Describe the location",
            helpText:
              "For example, the station you got off at or a specific bicycle parking",
          },
        },
      },
      kart: {
        beskrivelse: {
          label: "Describe the location",
          panelLabel: "Description",
        },
        breddegrad: "Latitude",
        lengdegrad: "Longitude",
      },
      ikkeFysiskSted: {
        label: "Describe where it happened",
        helpText: "For example SMS, website address, named dating app",
        seOverLabel: "Describe where it happened",
      },
      hvor: "Where dit it happen?",
      virksomhet: {
        helpText:
          "Address as in the Central Coordinating Register for Legal Entities",
        ingenAdresse: "No registered enterprise address",
      },
      freg: {
        helpText: "Address as in the National Population Register",
      },
      utlandet: {
        land: "Search and select country",
        postnummer: "Postal code (optional)",
        poststed: "Place/City (optional)",
        adresse: "Street name and house number (optional)",
      },
    },
    gjerningstidspunkt: {
      fra: {
        label: "From date and time",
        helpText: "dd.mm.yyyy hh:mm",
      },
      helpText: "If you are unsure, you can provide an approximate time",
      legend: "During what time period did it happen?",
      naar: "When did it happen?",
      til: {
        label: "To date and time",
        helpText: "dd.mm.yyyy hh:mm",
      },
    },
  },
  kvittering: {
    sideHeader: "Receipt for submitted crime report",
    forsikring: {
      info1: "You can use the case number",
      info2: "in contact with your insurance company",
      title: "Reported crime due to insurance?",
    },
    header: {
      info: "Click here to view your report",
      title: "Case number {{saksnummer}}",
    },
    bedrageriInfo: {
      info: "The scammer or scammers may try to scam you again by pretending to be someone who is going to help you with the case (police, bank or similar). Do not click on links or enter codes or passwords on pages that you have not yourself reached via a public website or online banking.",
      title: "Be careful",
    },
    hvaSkjerVidere: {
      info: "A summary of the report and a receipt confirming that we have received it will be sent to your digital mailbox or by post. A case officer will review your report and assess whether the police consider it appropriate to investigate the matter.",
      title: "What happens now?",
      link: "Read more about what happens when a case has been reported here",
    },
    nyeOpplysninger: {
      title: "New information?",
      info: "If you wish to withdraw your report or provide us with more information about the case, please contact the police district where the incident occurred and provide the case number {{saksnummer}}.",
    },
    knapper: {
      gaaTilOversikt: "Go to overview",
      til_oversikten: "To overview",
      opprett: "Create a new crime report",
    },
    saknr: "Criminal case number",
    sendtInn: "Submitted",
    takk: {
      info: "Your report helps us to get an overview of crime in our community. The information from your report helps to increase our knowledge and is used to prevent similar incidents from happening again",
      title: "Thank you for choosing to report the crime",
    },
    tittel: "Receipt",
  },
  navigerTilInnhold: "Skip to main content",
  omAnmelderOgFornaermet: {
    fornaermet: {
      konfliktraad: {
        info: {
          del1: "It may be appropriate to handle the case in the Norwegian Mediation Service. At the the Norwegian Mediation Service, the perpetrator and the victim meet after an offense, and it is often an effective means of preventing crime.",
          del2: "If the conditions for mediation through the Norwegian Mediation Service are met, you may need to sign a separate consent declaration later.",
          lenketekst:
            "You can read more about the Norwegian Mediation Service here.",
        },
        radioOptions: {
          JA: "Yes",
          NEI: "No",
          MER_INFO: "I want more information about the mediation service",
        },
        legacyRadioOptions: {
          VET_IKKE: "I do not now",
        },
        title: "Mediation service",
        fornaermetVilligTilKonfliktraad: {
          label:
            "Are you or the offended party willing to discuss this case in the Norwegian Mediation Service?",
        },
      },
      anmelder: {
        seg_selv: "Reporting on behalf of myself",
      },
      fornaermetType: {
        fornaermetTypeOptions: {
          ANMELDER: "My self",
          PERSON: "Another person",
          VIRKSOMHET: "An enterprise or organization",
        },
        label: "Who is the victim of the crime?",
      },
      person: {
        freg: {
          info: "To be able to add a victim that is not yourself, you must provide their full name and national identity number, and this information must match how the person is registered in the Norwegian Population Register",
          navn: {
            label: "Name",
            helpText:
              "It is important that you write the full name, as it is registered in the national population register.",
            valideringsfeil: "You must provide a name",
          },
          fnr: {
            label: "National identity number / D number",
            helpText: "Fill in 11 digits, without spaces or hyphens",
            valideringsfeil:
              "The national identity number must have exactly 11 digits",
          },
          submit: {
            label: "Search the National Population Register",
          },
          endre: {
            label: "Discard and search again",
          },
          error: {
            notFound: {
              title: "No matches found for name and birth number",
              message_zero:
                "You have searched too many times without success and have no more attempts left. Please try again later.",
              message_one:
                "We cannot find anyone in the National Population Register with this combination of name and personal identification number. Please make sure the information is correct and try again. You have {{count}} attempts left.",
              message_other:
                "We cannot find anyone in the National Population Register with this combination of name and personal identification number. Please make sure the information is correct and try again. You have {{count}} attempts left.",
            },
            forbidden: {
              title: "No more searches",
              message:
                "You have searched too many times without success and have no more attempts left. Please try again later.",
            },
            unknown: {
              title: "An unknown error has occured",
              message: "An unknown error has occured. Try again later.",
            },
          },
        },
        beskrivelse: {
          label: "Other information about the victim (optional)",
          se_over: "Other information about the victim",
        },
        epost: {
          label: "Email address (optional)",
          se_over: "Email address",
        },
        etternavn: {
          label: "Surname",
        },
        fornavn: {
          label: "First name",
        },
        relasjonstyper: {
          ANNET: "Other",
          FAMILIE: "Family",
          VERGE: "Legal guardian",
        },
        telefonnummer: {
          label: "Phone number",
        },
        tilknytning: {
          label: "What relationship do you have with the victim?",
        },
        tilknytningBeskrivelse: {
          label: "Describe your relation to the plaintiff",
          se_over: "Description of the relation",
        },
      },
      tittel: "About the victim of the crime",
      virksomhet: {
        valgtVirksomhet: {
          slett: "Delete",
          beliggenhetsadresse: "Location address",
          aktivitet: "Activity",
        },
        orgnummer: "Organization number",
        samtykkeTyper: {
          JA: "Yes",
          NEI: "No",
        },
        samtykkeVirksomhetKanSeAnmeldelsen: {
          label:
            "Would you like to send a copy of your report to the enterprise?",
          helptext:
            "The copy contains your contact information (not national identification number), as well as information about stolen/damaged items and where and when the incident occurred.",
        },
        relasjonstyper: {
          ANNET: "Other",
          ANSATT: "Employee",
          LEDER: "General manager or other management role",
          VEKTER: "Security guard",
        },
        sokefelt: {
          feilmeldingBrreg:
            "Could not contact the Central Coordinating Register, please try again later",
          helpText:
            "Search for name or organization number. Select the sub-unit where the offense occurred",
          hovedenhet: "Main entity",
          label: "Which enterprise are you reporting for?",
          underenhet: "Subunit",
        },
        tilknytning: {
          label: "What affiliation do you have to the enterprise?",
          se_over: "Affiliation to the enterprise",
        },
        tilknytningBeskrivelse: {
          label: "Describe your relation to the company",
        },
        tittel: "About the enterprise",
        vekterSelskap: {
          feilmeldingBrreg:
            "Could not contact the Central Coordinating Register, please try again later",
          helpText: "Search for name or organization number",
          label: "Which security company do you work for?",
          se_over: "Security company",
        },
        virksomhetensReferanse: {
          label: "Enterprise case reference",
          helpText:
            "Reference number for the enterprise's internal case processing, if applicable",
        },
      },
    },
    om_deg: {
      epost: "Email address",
      etternavn: "Surname",
      fnr: "National identity number / D number",
      fornavn: "First name",
      fregFeil: {
        henteknapp: "Try again",
        henter: "Retrieving",
        hjelpetekst: "Try again or come back later",
        tittel:
          "Unable to retrieve your name from the National Population Register",
      },
      hjelpetekst:
        "Your name is retrieved from the National Population Register.",
      mellomnavn: "Middle name",
      navn: "Name",
      skatteetaten:
        "If something is wrong, you must change the information in the National Population Register via skatteetaten.no.",
      telefon: "Phone number",
      tittel: "About you",
    },
    slett_anmeldelse: {
      modal: {
        avbryt: "Abort",
        hjelpetekst:
          "Are you sure you want to permanently delete the report you have begun to fill in? You cannot undo it.",
        label: "Delete draft?",
        slett: "Yes, delete",
      },
      slett: "Delete",
    },
  },
  oppsummering: {
    anmeldelse: "Submitted crime report",
    notifikasjon: {
      info: "You can request access to the criminal case file by contacting the police district where the case is being processed. The case is usually handled by the police district where the reported incident occurred.",
      tittel:
        "You cannot see the rest of the content of the report because it constitutes a confidential criminal document",
    },
    skrivUt: "Print the page",
    saknr: "Criminal case number",
    sendtInn: "Submitted",
    tittel: "Submitted crime report",
    diga: {
      mangler_saksnr: "Case number is missing, contact the police if needed",
      lovbrudd: {
        label: "Offense",
        type: {
          TYVERI_AV_SYKKEL: "Theft of bike",
          TYVERI_AV_MOBILTELEFON: "Theft of mobile phone",
          TYVERI_FLERE_GJENSTANDER: "Theft of other items",
          KJENNEMERKE: "Theft of licence plate",
          SKADEVERK: "Vandalism to private property, objects or vehicle",
          KRENKELSE: "Illegal use of real property",
          BRUKSTYVERI: "Theft or unauthorized use of vehicle or boat",
        } satisfies Record<DigaAnmeldelseType, string>,
      },
      gjerningssted: {
        type: {
          options: {
            ANNET_OFFENTLIG_STED: "Other public place in Norway",
            BUTIKK: "Shop or store in Norway",
            IDRETTS_ELLER_KULTURSTED: "Sports or cultural venue in Norway",
            KOLLEKTIVTRANSPORT: "Public transport in Norway",
            OMSORGSINSTITUSJON: "Care institution in Norway",
            SERVERINGSSTED: "Restaurant or serving establishment in Norway",
            UTLAND: "The incident occurred abroad",
            VED_BOPEL: "At or outside the residence in Norway",
          } satisfies Record<GjerningsstedType, string>,
          label: "Type of crime scene",
        },
        beskrivelse: "Description of the crime scene",
      },
      beskrivelse: "Description of the incident",
      gjenstandBeskrivelse: "Description of the item(s)",
      skadeverk: {
        type: {
          label: "Type of vandalism",
          options: {
            KJORETOY: "Vandalism to car, motorcycle, or moped",
            BYGNING: "Vandalism to or defacement of a building",
            KNUST_RUTE: "Vandalism involving broken windows",
            TAGGING: "Vandalism involving tagging or graffiti",
            BAAT: "Vandalism to boat",
            ANNET: "Other type of vandalism",
          } satisfies Record<SkadeverkType, string>,
        },
        beskrivelse: "Description of the vandalism",
      },
      brukstyveri: {
        type: {
          label: "Type of theft or unauthorized use",
          options: {
            KJORETOY: "Theft or unauthorized use of a motor vehicle",
            FARTOY: "Theft or unauthorized use of a vessel",
            ANNET:
              "Theft or unauthorized use of other motorized vehicle or vessel",
          } satisfies Record<BrukstyveriType, string>,
        },
        kjoretoyBeskrivelse: "Description of the theft or unauthorized use",
      },
    },
  },
  personSkjema: {
    legend: "Personal information",
    epost: "Email address",
    etternavn: "Surname",
    fjern: "Delete",
    foedselsdato: {
      helpText: "dd.mm.yyyy",
      label: "Date of birth",
    },
    foedselsEllerDnummer: {
      helpText: "National identity number / D number",
      label: "Personal ID (11 digits)",
    },
    fornavn: "First name",
    helptext:
      "You must fill in <strong>at least one</strong> of the fields, but please fill in as many as you can",
    telefon: "Phone number",
    ekstraKontaktopplysninger: {
      brukernavn: "Username",
      brukernavnContext: "Where was the username used?",
      brukernavnContextHelptext: "For example Finn.no, Whatsapp",
    },
  },
  se_over: {
    endre_steg: "Edit step",
    bekreftelseFalskAnmeldelse: {
      checkbox:
        "I am aware that it is a criminal offense to submit a false report to the police pursuant to sections 222 - 225 of the Norwegian Penal Code.",
      notifikasjon:
        "You must confirm that you understand your responsibility as a reporter of a crime",
    },
    gjerningsperson: {
      gjerningspersoner: "Information about the alleged perpetrator",
      label:
        "Do you have any information about the person or persons you think did it?",
    },
    hva_har_skjedd: "What happened?",
    hvordan_skjedde_det: "How dit it happen?",
    ingen_svar: "no answer",
    oppsummering_av_feil: {
      title:
        "The following fields must be filled in correctly before you can submit your report:",
    },
    modal: {
      feilet: "An error occurred while submitting",
      feiletInfo:
        "An error occurred and your report was not submitted. Please try again, or come back later.",
      lukk: "Close",
      provIgjen: "Try again",
      senderInn: "Submitting report...",
      suksess: "The report has been submitted",
    },
    om_deg: "About you",
    om_anmelder: "About you",
    om_fornaermede: "About the victim of the crime",
    om_virksomheten: "About the enterprise",
    send: "Submit crime report",
    tittel: {
      TYVERI_SKADEVERK_INNBRUDD: "Theft, burglary or damaged property",
      BEDRAGERI: "Fraud and ID theft",
    },
    vedlegg: {
      beskrivelse: "Description",
      filnavn: "File name",
    },
    vitnerlabel: "Contact information for the witness",
  },
  skjema_context: {
    lagrer: "Saving",
    sender_inn: "Submitting",
    sist_lagret: "Last saved",
    til_oversikten: "To overview",
    tittel: "Report a crime",
  },
  skjemaknapper: {
    gaa_tilbake: "Go to previous step",
    gaa_videre: "Go to next step",
  },
  standard: {
    valgfritt: "(optional)",
  },
  steg: {
    erstatningskrav: "Compensation claim",
    hva_har_skjedd: "What has happened?",
    hvem_gjorde_det: "Who did it?",
    hvem_saa_det: "Who has information?",
    hvor_og_naar: "Where and when did it happen?",
    konfliktraad: "Proceedings in The National Mediation Service",
    om_deg: "About you",
    om_deg_og_fornaermede: "About you and the victim of the crime",
    se_over: "Review and submit",
    vedlegg: "Attachments",
  },
  vedlegg: {
    beskrivelse: "Provide a brief description of the file (optional)",
    wasmSupport:
      "Your browser lacks the necessary functionality to upload files. Please update your browser or use a newer browser, e.g. Google Chrome",
    beskrivelseHjelpetekst:
      "Is the file a video? Please provide a time stamp that is relevant to take a look at.",
    hjelpetekst:
      "You can upload up to {{ maksAntallVedlegg }} files. Supported formats are {{ lovligeFiltyper }}.",
    infotekst: {
      BEDRAGERI: {
        bulletpoints: [
          "Screenshots of dialogue with the scammer or scammers",
          "Bank statement showing relevant transfers",
          "Screenshots of relevant online stores, advertisements or QR codes",
          "Audio recording of phone conversation",
          "Call log showing relevant phone call",
        ],
        hjelpetekst:
          "If you have a photo, video or other file that can help shed light on what happened, you can upload it here. Examples of attachments are:",
      },
      TYVERI_SKADEVERK_INNBRUDD: {
        bulletpoints: [
          "Surveillance video",
          "Photos or video clips that can identify the perpetrator",
          "Photos of damage",
          "Documentation of stolen or damaged items, such as receipts",
        ],
        hjelpetekst:
          "If you have a photo, video or other file that can help shed light on what happened, you can upload it here. Examples of attachments are:",
      },
      label: "Upload attachments (optional)",
    },
    lastOpp: "Upload attachments",
    opplastingFeilet: "Unable to save the attachment",
    slett: "Delete file",
    status: {
      KLARGJOER: "Preparing file for uploading",
      OPPRETTET: "Preparing file for uploading",
      LASTER_OPP: "Uploading",
      SCANNER: "Scanning file for virus",
      FEILET: "Failed",
      FEILET_FOR_OPPRETTELSE: "Failed",
      OPPLASTET: "Uploaded",
    },
    tittel: "Uploading attachment",
    lukkeUtenOpplasting:
      "If you leave this page, files that are uploading will be canceled. Are you sure you want to leave this page?",
  },
  samtykkeInformasjon: {
    infotekst: {
      bulletpoints: [
        "In order to investigate fraud and ID theft, it may be necessary to obtain additional information and documentation from private parties such as banks, credit companies or lending institutions. This may include information about your customer relationship or logs related to transactions or accounts stated in this report.",
        "We therefore ask for your consent to collect this information. Consent means that you exempt the aforementioned companies from any confidentiality obligations.",
        "It is not necessary to consent in order to submit a report.",
        "You can contact the police or the above-mentioned companies at any time and withdraw your consent, but the police may still choose to retain the information collected if it is necessary for the investigation.",
      ],
    },
    samtykkeInnhentingAvOpplysninger: {
      ja: "Yes",
      nei: "No",
      legend:
        "Do you consent that the police can obtain information and documentation about your customer/account relationships",
    },
  },
};
export default skjematekst;
